import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const OFFSCREEN = { top: -9999, left: -9999 };

/**
 * 把一個 position:fixed 的面板釘在某個表格格子（錨點）下方。
 * 劃假的精簡選單（MarkPicker）與日期明細（DayDetail）共用同一套定位規則，
 * ⛔ 不要各寫一份 —— 兩邊都踩過同樣的坑。
 *
 * ⚠️ 定位規則（task-8905 修正，別改回去）：
 * 舊版在 scroll／resize 時直接 onClose()，結果是「捲一下表格再點」的正常操作會讓面板
 * 一開就被關掉 —— 觸控板慣性捲動在放手後還會持續送 scroll 事件，使用者只會看到
 * 「點了沒反應」。現在改成 **重新定位而不是關閉**，只有錨點整個離開視窗或從 DOM
 * 消失才關。錨點用 selector 而不是快取的 element／rect，React 重繪後才不會抓到舊節點。
 *
 * placement：
 *   'below'（預設）＝ 面板置中掛在錨點正下方（劃假選單用，蓋住的是自己那一格）
 *   'beside'　　　　＝ 面板貼在錨點右側，⚠️ 當日明細要用這個 ——
 *                     明細是拿來跟「那一欄」對照的，面板蓋住自己描述的那一欄就本末倒置了。
 *                     右邊放不下會自動翻到左邊。
 *
 * 回傳 { panelRef, pos }：把 ref 掛在面板上、pos 當成 style 用。
 */
export const useAnchoredPanel = (anchorSelector, onClose, placement = 'below') => {
    const panelRef = useRef(null);
    const [pos, setPos] = useState(OFFSCREEN);

    /** 依錨點目前的位置重算面板座標；錨點不見了才回傳 false 讓外面關掉 */
    const reposition = useCallback(() => {
        if (!anchorSelector) return false;

        const anchor = document.querySelector(anchorSelector);
        if (!anchor) return false;

        const rect = anchor.getBoundingClientRect();
        // 寬高為 0 或整格捲出視窗＝錨點已經不在畫面上，這時才該關
        if (rect.width === 0 && rect.height === 0) return false;
        if (rect.bottom < 0 || rect.top > window.innerHeight) return false;
        if (rect.right < 0 || rect.left > window.innerWidth) return false;

        const panel = panelRef.current;
        const width = (panel && panel.offsetWidth) || 300;
        const height = (panel && panel.offsetHeight) || 200;

        let left;
        if (placement === 'beside') {
            left = rect.right + 8;
            // 右邊放不下就翻到錨點左邊；兩邊都放不下（窄螢幕）再退回貼齊視窗
            if (left + width > window.innerWidth - 8) left = rect.left - width - 8;
        } else {
            left = rect.left + rect.width / 2 - width / 2;
        }
        left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

        let top = rect.bottom + 6;
        if (top + height > window.innerHeight - 8) {
            top = Math.max(8, rect.top - height - 6);
        }

        setPos({ top, left });

        return true;
    }, [anchorSelector, placement]);

    useLayoutEffect(() => {
        if (!reposition()) {
            onClose();
        }
    }, [reposition, onClose]);

    useEffect(() => {
        let frame = null;

        const onViewportChange = () => {
            if (frame !== null) return;
            frame = window.requestAnimationFrame(() => {
                frame = null;
                if (!reposition()) {
                    onClose();
                }
            });
        };

        // capture:true —— 表格是自己捲的，捲動事件不會冒泡到 window
        window.addEventListener('scroll', onViewportChange, true);
        window.addEventListener('resize', onViewportChange);

        return () => {
            if (frame !== null) window.cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onViewportChange, true);
            window.removeEventListener('resize', onViewportChange);
        };
    }, [reposition, onClose]);

    // Esc 關閉：popover 現在不會自己被捲掉，就要給一個明確的退出方式
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return { panelRef, pos };
};

export default useAnchoredPanel;
